<template>
  <div class="export-formats-page">
    <PageHeader
      :title="text.exports.settings.page.title"
      :subtitle="text.exports.settings.page.subtitle"
      :actions="[]"
    />

    <section class="app-table-wrap export-formats-table-wrap">
      <div class="app-table-scroll">
        <table class="app-data-table">
          <thead>
            <tr>
              <th>{{ text.exports.settings.table.headers.id }}</th>
              <th>{{ text.exports.settings.table.headers.name }}</th>
              <th>{{ text.exports.settings.table.headers.module }}</th>
              <th>{{ text.exports.settings.table.headers.user }}</th>
              <th>{{ text.exports.settings.table.headers.columns }}</th>
              <th>{{ text.exports.settings.table.headers.updatedAt }}</th>
              <th>{{ text.exports.settings.table.headers.actions }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="format in rows" :key="format.id">
              <td>{{ Number(format?.id || 0) || '-' }}</td>
              <td>{{ resolveName(format) }}</td>
              <td>{{ resolveModuleLabel(format.module) }}</td>
              <td>{{ resolveUserLabel(format) }}</td>
              <td>{{ resolveColumnsCount(format) }}</td>
              <td>{{ formatDateHuman(format.updated_at) }}</td>
              <td class="app-table-actions-cell">
                <div class="app-table-actions-inline">
                  <button
                    type="button"
                    class="app-table-icon-btn app-table-icon-btn--view"
                    :title="text.exports.settings.table.actions.detail"
                    @click="openDetail(format)"
                  >
                    <i class="fa fa-eye" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    class="app-table-icon-btn app-table-icon-btn--view"
                    :title="text.exports.settings.table.actions.download"
                    @click="downloadFormat(format)"
                  >
                    <i class="fa fa-download" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    class="app-table-icon-btn app-table-icon-btn--delete"
                    :title="text.exports.settings.table.actions.delete"
                    :disabled="deletingId === Number(format?.id || 0)"
                    @click="openDeleteConfirm(format)"
                  >
                    <i class="fa fa-trash" aria-hidden="true"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="rows.length <= 0">
              <td colspan="7" class="app-table-empty">{{ text.exports.settings.table.empty }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <ExportFormatModal
      :show="isDetailModalOpen"
      :initial-format-id="detailFormatId"
      :lock-format-selector="true"
      :allow-export="detailModalAllowExport"
      :text-catalog="detailModalText"
      :export-service="detailModalService"
      :file-name-prefix="detailModalFileNamePrefix"
      @close="closeDetailModal"
    />

    <ConfirmActionModal
      :show="isDeleteConfirmOpen"
      :title="text.exports.settings.deleteModal.title"
      :subtitle="text.exports.settings.deleteModal.subtitle"
      :message="text.exports.settings.deleteModal.message(resolveName(pendingDeleteFormat || {}))"
      :confirm-text="text.exports.settings.deleteModal.confirm"
      :cancel-text="text.actions.common.cancel"
      @confirm="confirmDeleteFormat"
      @cancel="closeDeleteConfirm"
    />
  </div>
</template>

<script>
import PageHeader from '@/components/common/PageHeader.vue';
import ExportFormatModal from '@/components/common/exports/ExportFormatModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import customersService from '@/services/customers';
import suppliersService from '@/services/suppliers';
import simpleNotesService from '@/services/simpleNotes';
import paymentReceiptsService from '@/services/paymentReceipts';
import expenseReceiptsService from '@/services/expenseReceipts';
import invoicesService from '@/services/invoices';
import financialAccountsService from '@/services/financialAccounts';
import exportsService from '@/services/exports';
import { extractApiErrorMessage, extractCollection } from '@/utils/dataNormalization';
import { formatDateTime } from '@/utils/datePresentation';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('customers');
const { catalog: suppliersText } = useDomainText('suppliers');
const { catalog: simpleNotesText } = useDomainText('simpleNotes');
const { catalog: paymentReceiptsText } = useDomainText('paymentReceipts');
const { catalog: expenseReceiptsText } = useDomainText('expenseReceipts');
const { catalog: invoicesText } = useDomainText('invoices');
const { catalog: financialText } = useDomainText('financial');

export default {
  name: 'ExportFormatsPage',
  components: {
    PageHeader,
    ExportFormatModal,
    ConfirmActionModal,
  },
  data() {
    return {
      text,
      suppliersText,
      simpleNotesText,
      paymentReceiptsText,
      expenseReceiptsText,
      invoicesText,
      financialText,
      rows: [],
      isDetailModalOpen: false,
      detailFormatId: 0,
      detailModuleKey: 'customers',
      deletingId: 0,
      pendingDeleteFormat: null,
      isDeleteConfirmOpen: false,
    };
  },
  computed: {
    detailModalText() {
      return this.resolveTextByModule(this.detailModuleKey);
    },
    detailModalService() {
      return this.resolveServiceByModule(this.detailModuleKey);
    },
    detailModalFileNamePrefix() {
      return this.resolveFilePrefixByModule(this.detailModuleKey);
    },
    detailModalAllowExport() {
      return this.detailModuleKey !== 'financial_accounts';
    },
  },
  mounted() {
    this.fetchFormats();
  },
  methods: {
    resolveServiceByModule(moduleKey) {
      const key = String(moduleKey || '').trim();
      if (key === 'suppliers') {
        return suppliersService;
      }

      if (key === 'simple_notes') {
        return simpleNotesService;
      }

      if (key === 'payment_receipts') {
        return paymentReceiptsService;
      }

      if (key === 'payment_expenses') {
        return expenseReceiptsService;
      }

      if (key === 'invoices') {
        return invoicesService;
      }

      if (key === 'financial_accounts') {
        return financialAccountsService;
      }

      return customersService;
    },
    resolveTextByModule(moduleKey) {
      const key = String(moduleKey || '').trim();
      if (key === 'suppliers') {
        return this.suppliersText;
      }

      if (key === 'simple_notes') {
        return this.simpleNotesText;
      }

      if (key === 'payment_receipts') {
        return this.paymentReceiptsText;
      }

      if (key === 'payment_expenses') {
        return this.expenseReceiptsText;
      }

      if (key === 'invoices') {
        return this.invoicesText;
      }

      if (key === 'financial_accounts') {
        return this.financialText;
      }

      return this.text;
    },
    resolveFilePrefixByModule(moduleKey) {
      const key = String(moduleKey || '').trim();
      if (key === 'suppliers') {
        return 'proveedores';
      }

      if (key === 'simple_notes') {
        return 'notas_simples';
      }

      if (key === 'payment_receipts') {
        return 'pagos_ingresos';
      }

      if (key === 'payment_expenses') {
        return 'pagos_egresos';
      }

      if (key === 'invoices') {
        return 'facturas';
      }

      if (key === 'financial_accounts') {
        return 'movimientos_cuentas_financieras';
      }

      return 'clientes';
    },
    resolveModuleToastText(moduleKey) {
      const moduleText = this.resolveTextByModule(moduleKey);
      const toast = moduleText?.exports?.modal?.toast;

      return toast && typeof toast === 'object'
        ? toast
        : (this.text?.exports?.modal?.toast || {});
    },
    async fetchFormats() {
      try {
        const response = await exportsService.formats({
          modules: ['customers', 'suppliers', 'simple_notes', 'payment_receipts', 'payment_expenses', 'invoices', 'financial_accounts'],
        });
        const rows = extractCollection(response);

        this.rows = (Array.isArray(rows) ? rows : [])
          .map((row) => ({
            ...row,
            module: String(row?.module || '').trim() || 'customers',
          }))
          .sort((left, right) => {
            const leftUpdated = new Date(left?.updated_at || left?.created_at || 0).getTime();
            const rightUpdated = new Date(right?.updated_at || right?.created_at || 0).getTime();

            return rightUpdated - leftUpdated;
          });
      } catch (error) {
        this.rows = [];
        this.$toast?.error(this.text.exports.settings.toast.loadError);
      }
    },
    resolveName(format) {
      return String(format?.name || '').trim() || '-';
    },
    resolveColumnsCount(format) {
      const columns = Array.isArray(format?.columns) ? format.columns : [];
      return columns.length;
    },
    resolveModuleLabel(moduleKey) {
      const key = String(moduleKey || '').trim();
      const map = this.text.exports.settings.table.moduleLabelByKey || {};
      if (map[key]) {
        return map[key];
      }

      if (key === 'simple_notes') {
        return 'Notas simples';
      }

      if (key === 'suppliers') {
        return 'Proveedores';
      }

      if (key === 'payment_receipts') {
        return 'Pagos de ingresos';
      }

      if (key === 'payment_expenses') {
        return 'Pagos de egresos';
      }

      if (key === 'invoices') {
        return 'Facturas';
      }

      if (key === 'financial_accounts') {
        return 'Cuentas financieras';
      }

      return map[key] || key || '-';
    },
    resolveUserLabel(format) {
      const name = String(format?.created_by_name || '').trim();
      return name !== '' ? name : '-';
    },
    formatDateHuman(value) {
      if (!value) {
        return '-';
      }

      return formatDateTime(value, { showTime: true });
    },
    openDetail(format) {
      const formatId = Number(format?.id || 0);
      if (formatId <= 0) {
        return;
      }

      this.detailFormatId = formatId;
      this.detailModuleKey = String(format?.module || 'customers').trim() || 'customers';
      this.isDetailModalOpen = true;
    },
    closeDetailModal() {
      this.isDetailModalOpen = false;
      this.detailFormatId = 0;
      this.detailModuleKey = 'customers';
      this.fetchFormats();
    },
    openDeleteConfirm(format) {
      const formatId = Number(format?.id || 0);
      if (formatId <= 0) {
        return;
      }

      this.pendingDeleteFormat = format;
      this.isDeleteConfirmOpen = true;
    },
    closeDeleteConfirm() {
      this.pendingDeleteFormat = null;
      this.isDeleteConfirmOpen = false;
    },
    async downloadFormat(format) {
      const columns = Array.isArray(format?.columns) ? format.columns : [];
      const moduleKey = String(format?.module || '').trim() || 'customers';
      const moduleToast = this.resolveModuleToastText(moduleKey);
      if (moduleKey === 'financial_accounts') {
        this.$toast?.error('Para exportar movimientos financieros abre el detalle de la cuenta y usa Exportar.');
        return;
      }

      if (columns.length <= 0) {
        this.$toast?.error(moduleToast.noColumns || this.text.exports.modal.toast.noColumns);
        return;
      }

      try {
        const blob = await exportsService.exportExcel({
          module: moduleKey,
          scope: 'all',
          selected_columns: columns,
          format_name: String(format?.name || '').trim(),
        });

        if (!(blob instanceof Blob)) {
          throw new Error(moduleToast.exportError || this.text.exports.modal.toast.exportError);
        }

        const downloadUrl = window.URL.createObjectURL(blob);
        const fileName = `${this.resolveFilePrefixByModule(moduleKey)}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        this.$toast?.success(moduleToast.exportSuccess || this.text.exports.modal.toast.exportSuccess);
      } catch (error) {
        const message = extractApiErrorMessage(error?.response)
          || error?.response?.data?.message
          || moduleToast.exportError
          || this.text.exports.modal.toast.exportError;
        this.$toast?.error(message);
      }
    },
    async confirmDeleteFormat() {
      const format = this.pendingDeleteFormat;
      const formatId = Number(format?.id || 0);
      const moduleKey = String(format?.module || '').trim() || 'customers';
      const moduleToast = this.resolveModuleToastText(moduleKey);
      if (formatId <= 0) {
        this.closeDeleteConfirm();
        return;
      }

      this.deletingId = formatId;

      try {
        await exportsService.deleteFormat(formatId);
        await this.fetchFormats();
        this.$toast?.success(moduleToast.formatDeleted || this.text.exports.modal.toast.formatDeleted);
      } catch (error) {
        const message = extractApiErrorMessage(error?.response)
          || error?.response?.data?.message
          || moduleToast.formatDeleteError
          || this.text.exports.modal.toast.formatDeleteError;
        this.$toast?.error(message);
      } finally {
        this.deletingId = 0;
        this.closeDeleteConfirm();
      }
    },
  },
};
</script>

<style scoped>
.export-formats-table-wrap {
  margin-top: 14px;
}
</style>
